import React, { FC, memo, useEffect } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Loader, TableFooter,
  Typography, TableSortLabel,
} from '@trackman/web-shared-components';
import { t } from 'utilities/TranslationUtils';

// utils
import { GetColumnFilterComponent } from 'components/TableFilters/TableFilters';
import { AppSectionErrorBoundary } from 'components/ui';
import {tableHooks} from "./WSC-v2/tableHooks";

interface STProps {
  columns: any[];
  data: any[];
  rowMapper?: (cells: any[]) => JSX.Element;
  rowsPerPage: number;
  FiltersComponent?: any;
  noResultMessage?: string;
  isLoading?: boolean;
  hideFooter?: boolean;
  hideFilters?: boolean;
  disableResettingPaginationOnUpdate?: boolean;
  children?: React.ReactNode;
}

export const DataTable: FC<STProps> = memo((props: STProps) => {
  const {
    columns,
    data,
    rowMapper,
    rowsPerPage,
    isLoading,
    FiltersComponent,
    hideFooter = false,
    disableResettingPaginationOnUpdate = false,
    children = null,
  } = props;
  const { useTable, useSortBy, usePagination, useFlexLayout } = tableHooks;

  const [pageLocal, setPageLocal] = React.useState(1);

  const onChangePageLocal = (pageLocal: number) => {
    setPageLocal(pageLocal);
  };

  useEffect(() => {
    !disableResettingPaginationOnUpdate && setPageLocal(1);
  }, [data]);

  const dataMemorized = React.useMemo(() => {
    return data?.slice((pageLocal - 1) * rowsPerPage, pageLocal * rowsPerPage);
  }, [data, pageLocal, rowsPerPage]);

  const { getTableProps, rows, headerGroups, prepareRow } = useTable(
    {
      columns: columns,
      data: dataMemorized,
    },
    useSortBy,
    usePagination,
    useFlexLayout
  );

  return (
    <AppSectionErrorBoundary>
      <TableContainer>
        <>
          <div className='current-filters-container'>
            {FiltersComponent}
            {children}
          </div>
        </>

        <Table className='data-table' {...getTableProps()} style={{}}>
          <TableHead>
            {headerGroups.map(({ headers, getHeaderGroupProps }) => (
              <TableRow {...getHeaderGroupProps()} style={{display:'flex'}}>
                {headers.map(({ sortable, isSorted, isSortedDesc, align, render, getHeaderProps, getSortByToggleProps }, index) => {
                  const column = columns[index];
                  return column?.sorter ? (
                    <TableCell
                      {...getHeaderProps(sortable && getSortByToggleProps())}
                      sortable={column?.sorter}
                      onClick={(e) => {
                        if (column?.sorter) {
                          column?.sorter?.onSort();
                          setPageLocal(1);
                        }
                      }}
                      sortDirection={column?.sorter ? column?.sorter.getValue() : false}
                      align={align}
                    >
                      <TableSortLabel active={column?.sorter.isActive()} direction={column?.sorter.getValue()}>
                        <div className='data-table-header'>
                          {render('Header')}
                          {column.filter && GetColumnFilterComponent(column.filter, (value) => setPageLocal(1))}
                        </div>
                      </TableSortLabel>
                    </TableCell>
                  ) : (
                    <TableCell
                      {...getHeaderProps(sortable && getSortByToggleProps())}
                      sortable={sortable}
                      sortDirection={isSorted ? (isSortedDesc ? 'desc' : 'asc') : false}
                      align={align}
                    >
                      <div className='data-table-header'>
                        {render('Header')}
                        {column.filter && GetColumnFilterComponent(column.filter, (value) => setPageLocal(1))}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <div style={{ height: '455px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader size='large' />
                  </div>
                </TableCell>
              </TableRow>
            ) : rows.length !== 0 ? (
              rows.map((row, index) => {
                prepareRow(row);
                return (
                  <TableRow index={index} {...row.getRowProps()} style={{display:'flex'}}>
                    {rowMapper
                      ? rowMapper(row.cells)
                      : row.cells.map(({ getCellProps, column, render }) => (
                        <TableCell className='table-body-cell-nopointer' {...getCellProps()} align={column.align}>
                          {render('Cell')}
                        </TableCell>
                      ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} style={{ textAlign: 'center', margin: '16px' }}>
                  <div style={{ textAlign: 'center', margin: '50px', justifyContent: 'center' }} className='side-title'>
                    {t('table.no_data')}
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {hideFooter || data.length === 0 ? (
            <></>
          ) : (
            <TableFooter>
              <TableRow>
                <TablePagination
                  page={pageLocal - 1}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[rowsPerPage]}
                  onRowsPerPageChange={undefined}
                  count={data.length}
                  onPageChange={(_, p) => onChangePageLocal(p + 1)}
                  renderPages={() => <Typography> {pageLocal} of {Math.ceil(data.length/rowsPerPage)} </Typography>}
                />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </AppSectionErrorBoundary>
  );
});
